import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import type { AppDispatch } from '../../redux/store';
import { useCampaigns } from '../../hooks/useCampaigns';
import CampaignDetailModal from './CampaignDetailModal';
import { ShareModal } from '../scheduleCampaign';
import LoadingState from '../common/LoadingState';
import PlatformIcon from './PlatformIcon';
import StatusIndicator from './StatusIndicator';
import { formatDateForInput, getCampaignName, getCampaignPlatforms, getMonthDates, getWeekDates, getCampaignsForDate, parseSocialMedia, getCreatedAt, getStartDate, getEndDate, isCampaignLive, getCampaignStatus } from './calendarUtils';
import { formatLocalTime } from '../../utils/dateUtils';

type ViewMode = 'month' | 'week' | 'day';

const MarketingCalendar: React.FC = () => {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const { campaigns, isInitialLoading: loading } = useCampaigns();
  const [selectedCampaign, setSelectedCampaign] = useState<any>(null);

  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [clickPosition, setClickPosition] = useState<{ x: number; y: number } | null>(null);
  const [expandedCells, setExpandedCells] = useState<Set<string>>(new Set());
  const [showViewDropdown, setShowViewDropdown] = useState(false);

  const handleEventClick = (campaign: any, event: React.MouseEvent) => {
    // If it's a scheduled campaign (not yet posted), open schedule modal
    if (campaign._type === 'scheduled') {
      setSelectedCampaign(campaign);
      setIsScheduleModalOpen(true);
      return;
    }
    
    // Otherwise open the detail modal for posted campaigns
    setSelectedCampaign(campaign);
    setClickPosition({ x: event.clientX, y: event.clientY });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCampaign(null);
    setClickPosition(null);
  };

  const handleCloseScheduleModal = () => {
    setIsScheduleModalOpen(false);
    setSelectedCampaign(null);
  };

  const getInitialScheduleData = (campaign: any) => {
    if (!campaign?.scheduleData) return undefined;

    const scheduleDate = campaign.scheduleData.scheduleDate 
      ? new Date(campaign.scheduleData.scheduleDate)
      : null;

    const platforms = campaign.scheduleData.channels?.map((ch: any) => 
      (typeof ch === 'string' ? ch : ch.name || '').toLowerCase()
    ) || [];

    if (!scheduleDate) return undefined;

    return {
      fromDate: scheduleDate.toISOString().split('T')[0],
      toDate: '',
      time: `${String(scheduleDate.getHours()).padStart(2, '0')}:${String(scheduleDate.getMinutes()).padStart(2, '0')}`,
      selectedMediums: platforms as any[]
    };
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    setSelectedDate(newDate);
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    setSelectedDate(newDate);
  };

  const navigateDay = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    setSelectedDate(newDate);
  };


  const toggleCellExpansion = (dateKey: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedCells(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dateKey)) {
        newSet.delete(dateKey);
      } else {
        newSet.add(dateKey);
      }
      return newSet;
    });
  };

  const getCellDateKey = (date: Date) => formatDateForInput(date);

  const renderWeekView = () => {
    const weekDates = getWeekDates(selectedDate);
    const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    return (
      <div>
        <div className="grid grid-cols-7 gap-3">
          {weekDates.map((date, idx) => {
            const dayCampaigns = getCampaignsForDate(campaigns, date);
            const isToday = date.toDateString() === new Date().toDateString();

            return (
              <div key={idx} className="flex flex-col">
                <div className={`text-center p-3 rounded-t-lg border-b-2 ${isToday
                  ? 'bg-surface-medium text-white'
                  : 'bg-surface-medium border-[#E0E0E040]'
                  }`}>
                  <div className="text-xs font-normal text-white">
                    {weekDays[idx].substring(0, 3)}
                  </div>
                  <div className="text-lg font-medium text-white">
                    {date.getDate()}
                  </div>
                </div>

                <div className="flex-1 border border-t-0 rounded-b-lg p-2 min-h-[70vh] bg-surface-dark border-[#E0E0E040]">
                  {dayCampaigns.length > 0 ? (
                    <div className="space-y-2">
                      {dayCampaigns.map((campaign: any) => {
                        const title = getCampaignName(campaign);
                        const platforms = getCampaignPlatforms(campaign);
                        const isLive = isCampaignLive(campaign);
                        const isScheduled = campaign._type === 'scheduled';

                        return (
                          <div
                            key={campaign._uniqueKey || campaign.id}
                            onClick={(e) => handleEventClick(campaign, e)}
                            className={`text-xs p-2 rounded border cursor-pointer transition-all relative ${
                              isScheduled 
                                ? 'bg-[#1a2a3a] border-[#3b82f6] hover:bg-[#1f3142]' 
                                : isLive 
                                  ? 'bg-[#313131] border-[#E0E0E021] hover:bg-[#2f2f2f]'
                                  : 'bg-[#313131] border-green-500/50 hover:bg-[#2f2f2f]'
                            }`}
                          >
                            <StatusIndicator status={getCampaignStatus(campaign)} campaign={campaign} />
                            {isScheduled && (
                              <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-blue-500 text-white text-[8px] font-semibold rounded">
                                SCHEDULED
                              </div>
                            )}
                            {isLive && !isScheduled && (
                              <div className="absolute top-1 right-1 w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                            )}
                            <div className="flex items-start gap-1 mb-1">
                              {platforms.slice(0, 3).map((platform, idx) => (
                                <PlatformIcon key={idx} platform={platform} />
                              ))}
                              <span className="text-[10px] text-[#ffffff70]">
                                {getCreatedAt(campaign) && formatLocalTime(getCreatedAt(campaign))}
                              </span>
                            </div>
                            <p className="text-[10px] font-normal line-clamp-2 leading-tight text-white">
                              {title}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400">
                      <div className="text-center">
                        <svg className="w-8 h-8 mx-auto mb-2 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-xs">No campaigns</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const dayCampaigns = getCampaignsForDate(campaigns, selectedDate);
    const isToday = selectedDate.toDateString() === new Date().toDateString();

    return (
      <div>
        <div className={`p-4 rounded-xl mb-3 ${isToday
          ? 'bg-surface-medium text-white'
          : 'bg-surface-medium'
          }`}>
          <div className="text-lg font-medium mb-1 text-white">
            {selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            <p className="text-xs font-normal mb-1 text-white">
              {selectedDate.toLocaleDateString('en-US', { weekday: 'long' })}
            </p>
          </div>
          {dayCampaigns.length > 0 && (
            <div className="text-sm mt-2 text-white">
              {dayCampaigns.length} {dayCampaigns.length === 1 ? 'campaign' : 'campaigns'} scheduled
            </div>
          )}
        </div>

        {dayCampaigns.length > 0 ? (
          <div className="space-y-3">
            {dayCampaigns.map((campaign: any) => {
              const title = getCampaignName(campaign);
              const platforms = getCampaignPlatforms(campaign);
              const isScheduled = campaign._type === 'scheduled';

              return (
                <div
                  key={campaign._uniqueKey || campaign.id}
                  onClick={(e) => handleEventClick(campaign, e)}
                  className={`p-5 rounded-xl border cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-[1.01] relative ${
                    isScheduled 
                      ? 'bg-[#1a2a3a] border-[#3b82f6] hover:border-[#60a5fa]' 
                      : 'bg-surface-dark border-[#E0E0E040] hover:border-[#E0E0E021]'
                  }`}
                >
                  {isScheduled && (
                    <div className="absolute top-3 right-3 px-2 py-1 bg-blue-500 text-white text-xs font-semibold rounded">
                      SCHEDULED
                    </div>
                  )}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-1">
                        <h3 className="font-bold text-lg text-white">{title}</h3>
                      </div>

                      <div className="space-y-2 text-sm">
                        <div className="flex items-center space-x-2">
                          <div className="flex gap-1">
                            {platforms.map((platform, idx) => (
                              <PlatformIcon key={idx} platform={platform} />
                            ))}
                          </div>
                          <span className="text-xs text-gray-300">
                            {platforms.map(p => p.trim()).join(', ')}
                          </span>
                        </div>

                        {(() => {
                          const startDate = getStartDate(campaign);
                          const endDate = getEndDate(campaign);
                          if (startDate && endDate) {
                            return (
                              <div className="flex items-center space-x-2 text-gray-300">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span className="text-xs">
                                  {formatDateForInput(new Date(startDate))} - {formatDateForInput(new Date(endDate))}
                                </span>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16 rounded-xl bg-surface-dark border border-[#E0E0E040]">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 bg-surface-medium">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="font-medium text-lg text-white">No campaigns scheduled for this day</p>
            <p className="text-sm mt-2 mb-6 text-gray-400">Schedule a campaign to see it here</p>
          </div>
        )}
      </div>
    );
  };

  const renderMonthView = () => {
    const monthDates = getMonthDates(selectedDate);
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
      <div>
        <div className="grid grid-cols-7 gap-1 mb-1">
          {weekDays.map((day) => (
            <div key={day} className="text-center text-sm  font-normal py-3 text-[#fff] bg-surface-medium rounded-t-lg">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {monthDates.map(({ date, currentMonth }, idx) => {
            const dayCampaigns = getCampaignsForDate(campaigns, date);
            const hasCampaigns = dayCampaigns.length > 0;
            const isToday = date.toDateString() === new Date().toDateString();

            return (
              <div
                key={idx}
                className={`min-h-[120px] p-2 rounded-lg border transition-all duration-200 ${!currentMonth
                  ? 'bg-surface-dark border-[#E0E0E021] opacity-20'
                  : hasCampaigns
                    ? 'bg-surface-dark border-[#E0E0E040] cursor-pointer'
                    : isToday
                      ? 'bg-[#313131] border-[#ee442370]'
                      : 'bg-surface-dark border-[#E0E0E040]'
                  }`}
              >
                <div className={`text-sm font-normal mb-2 ${hasCampaigns ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                  {date.getDate()}
                </div>
                {hasCampaigns && (
                  <div className="space-y-1">
                    {dayCampaigns.slice(0, 2).map((campaign: any) => {
                      const title = getCampaignName(campaign);
                      const platforms = getCampaignPlatforms(campaign);
                      const isLive = isCampaignLive(campaign);
                      const isScheduled = campaign._type === 'scheduled';

                      return (
                        <div
                          key={campaign._uniqueKey || campaign.id}
                          onClick={(e) => handleEventClick(campaign, e)}
                          className={`text-xs p-1.5 rounded border cursor-pointer transition-all relative ${
                            isScheduled 
                              ? 'bg-[#1a2a3a] border-[#3b82f6] hover:bg-[#1f3142]' 
                              : isLive
                                ? 'bg-[#313131] border-[#E0E0E021] hover:bg-[#2f2f2f]'
                                : 'bg-[#313131] border-green-500/50 hover:bg-[#2f2f2f]'
                          }`}
                        >
                          <StatusIndicator status={getCampaignStatus(campaign)} campaign={campaign} />
                          {isScheduled && (
                            <div className="absolute top-0.5 right-0.5 px-1 py-0.5 bg-blue-500 text-white text-[7px] font-semibold rounded">
                              SCH
                            </div>
                          )}
                          {isLive && !isScheduled && (
                            <div className="absolute top-1 right-1 w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                          )}
                          <div className="flex items-start gap-1 mb-1">
                            {platforms.map((platform, idx) => (
                              <PlatformIcon key={idx} platform={platform} />
                            ))}
                            <span className="text-[10px] text-[#ffffff70]">
                              {getCreatedAt(campaign) && formatLocalTime(getCreatedAt(campaign))}
                            </span>
                          </div>
                          <p className="text-[10px] font-normal line-clamp-2 leading-tight text-white">
                            {title}
                          </p>
                        </div>
                      );
                    })}

                    {dayCampaigns.length > 2 && (
                      <div
                        className={`overflow-hidden transition-all duration-300 ease-in-out space-y-1 ${expandedCells.has(getCellDateKey(date)) ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                          }`}
                      >
                        {dayCampaigns.slice(2).map((campaign: any) => {
                          const title = getCampaignName(campaign);
                          const platforms = getCampaignPlatforms(campaign);
                          const isLive = isCampaignLive(campaign);
                          const isScheduled = campaign._type === 'scheduled';

                          return (
                            <div
                              key={campaign._uniqueKey || campaign.id}
                              onClick={(e) => handleEventClick(campaign, e)}
                              className={`text-xs p-1.5 rounded border cursor-pointer transition-all relative ${
                                isScheduled 
                                  ? 'bg-[#1a2a3a] border-[#3b82f6] hover:bg-[#1f3142]' 
                                  : isLive
                                    ? 'bg-[#313131] border-[#212121] hover:bg-[#2f2f2f]'
                                    : 'bg-[#313131] border-green-500/50 hover:bg-[#2f2f2f]'
                              }`}
                            >
                              <StatusIndicator status={getCampaignStatus(campaign)} campaign={campaign} />
                              {isScheduled && (
                                <div className="absolute top-0.5 right-0.5 px-1 py-0.5 bg-blue-500 text-white text-[7px] font-semibold rounded">
                                  SCH
                                </div>
                              )}
                              {isLive && !isScheduled && (
                                <div className="absolute top-1 right-1 w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                              )}
                              <div className="flex items-start gap-1 mb-1">
                                {platforms.map((platform, idx) => (
                                  <PlatformIcon key={idx} platform={platform} />
                                ))}
                                <span className="text-[10px] text-[#ffffff70]">
                                  {getCreatedAt(campaign) && formatLocalTime(getCreatedAt(campaign))}
                                </span>
                              </div>
                              <p className="text-[10px] font-normal line-clamp-2 leading-tight text-white">
                                {title}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {dayCampaigns.length > 2 && (
                      <button
                        onClick={(e) => toggleCellExpansion(getCellDateKey(date), e)}
                        className="text-xs pl-1 mt-1 hover:underline cursor-pointer font-medium transition-colors duration-50 text-gray-400 hover:text-gray-200"
                      >
                        {expandedCells.has(getCellDateKey(date)) ? 'Show less' : `+${dayCampaigns.length - 2} more`}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return <LoadingState message="Loading campaigns..." />;
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-full mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-brand-primary shadow-lg">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Scheduled Campaigns</h2>
              <p className="text-xs text-gray-400">View and manage your scheduled campaigns</p>
            </div>
          </div>

          {/* View Controls */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center rounded-lg px-1 py-[2px] bg-surface-dark border border-gray-700">
              <button
                onClick={() => {
                  if (viewMode === 'month') navigateMonth('prev');
                  else if (viewMode === 'week') navigateWeek('prev');
                  else navigateDay('prev');
                }}
                className="p-1.5 rounded-md cursor-pointer transition-colors hover:bg-orange-900/30"
              >
                <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <button
                onClick={() => setSelectedDate(new Date())}
                className="px-3 py-1 rounded-md transition-all text-[11px] font-medium mx-1 bg-brand-primary text-white shadow-sm"
              >
                {selectedDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
              </button>

              <button
                onClick={() => {
                  if (viewMode === 'month') navigateMonth('next');
                  else if (viewMode === 'week') navigateWeek('next');
                  else navigateDay('next');
                }}
                className="p-1.5 rounded-md cursor-pointer transition-colors hover:bg-orange-900/30"
              >
                <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* View Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowViewDropdown(!showViewDropdown)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-surface-dark text-gray-300 border border-gray-700 hover:border-gray-600 transition-all min-w-[100px] justify-between"
              >
                <span className="capitalize">{viewMode}</span>
                <svg className={`w-4 h-4 transition-transform ${showViewDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showViewDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowViewDropdown(false)}
                  />
                  <div className="absolute right-0 mt-2 w-32 rounded-lg shadow-lg bg-[#2a2a2a] border border-gray-700 overflow-hidden z-20">
                    {(['month', 'week', 'day'] as ViewMode[]).map((mode) => (
                      <button
                        key={mode}
                        onClick={() => {
                          setViewMode(mode);
                          setShowViewDropdown(false);
                        }}
                        className={`w-full px-4 py-2 text-left text-xs font-medium capitalize transition-colors ${viewMode === mode
                          ? 'bg-brand-primary text-white'
                          : 'text-gray-300 hover:bg-[#3a3a3a]'
                          }`}
                      >
                        {mode}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Calendar content */}
        <div
          className="rounded-lg p-4 shadow-sm border bg"
          style={{
            backdropFilter: 'blur(10px)',
            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 14px 0px',
            border: '1px solid rgba(224, 224, 224, 0.13)'
          }}
        >
          {viewMode === 'month' && renderMonthView()}
          {viewMode === 'week' && renderWeekView()}
          {viewMode === 'day' && renderDayView()}
        </div>

        <CampaignDetailModal
          campaign={selectedCampaign}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          clickPosition={clickPosition}
        />

        <ShareModal
          key={selectedCampaign?.id || 'share-modal'}
          isOpen={isScheduleModalOpen}
          onClose={handleCloseScheduleModal}
          flyerImageUrl={selectedCampaign?.flyer?.image?.storage_url || ''}
          campaignId={selectedCampaign?.id}
          socialMediaData={selectedCampaign?.channels || []}
          restaurantId={restaurantId || ''}
          initialOption="schedule-later"
          initialScheduleData={selectedCampaign ? getInitialScheduleData(selectedCampaign) : undefined}
        />
      </div>
    </div>
  );
};

export default MarketingCalendar;
